import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { ChevronDown, Loader2, Send } from "lucide-react";
import { API, auth, guarded, LEAD_STAGES, TEAM, TOPICS, chipCls, inputCls, label, selectCls } from "./shared";

export default function Leads({ token, onAuthError }) {
  const [items, setItems] = useState(null);
  const [stageF, setStageF] = useState("");
  const [topicF, setTopicF] = useState("");
  const [openId, setOpenId] = useState(null);
  const [note, setNote] = useState("");

  const load = useCallback(
    () =>
      guarded(async () => {
        const { data } = await axios.get(`${API}/enquiries`, auth(token));
        setItems(data);
      }, onAuthError),
    [token, onAuthError]
  );

  useEffect(() => {
    load();
  }, [load]);

  const patch = (id, body) =>
    guarded(async () => {
      await axios.patch(`${API}/enquiries/${id}`, body, auth(token));
      await load();
    }, onAuthError);

  if (!items) {
    return (
      <div className="flex items-center gap-3 py-20 text-[#8A837B]">
        <Loader2 size={18} className="animate-spin" /> Loading leads…
      </div>
    );
  }

  const filtered = items.filter(
    (q) => (!stageF || (q.stage || "new") === stageF) && (!topicF || q.topic === topicF)
  );

  return (
    <div data-testid="leads-tab">
      <div className="mb-6 flex flex-wrap items-center gap-3">
        <select data-testid="leads-filter-stage" value={stageF} onChange={(e) => setStageF(e.target.value)} className={selectCls}>
          <option value="">All stages</option>
          {LEAD_STAGES.map((s) => (
            <option key={s} value={s}>{label(s)}</option>
          ))}
        </select>
        <select data-testid="leads-filter-topic" value={topicF} onChange={(e) => setTopicF(e.target.value)} className={selectCls}>
          <option value="">All topics</option>
          {TOPICS.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
        <span className="text-xs text-[#8A837B]" data-testid="leads-count">{filtered.length} lead{filtered.length === 1 ? "" : "s"}</span>
      </div>

      <div className="border border-[#E6E2DC] bg-white">
        {filtered.length === 0 && <p className="p-8 text-sm text-[#8A837B]">No leads match these filters.</p>}
        {filtered.map((q) => {
          const stage = q.stage || "new";
          const open = openId === q.id;
          return (
            <div key={q.id} className="border-b border-[#E6E2DC] last:border-b-0" data-testid={`lead-row-${q.id}`}>
              <div className="grid grid-cols-2 items-center gap-3 px-5 py-4 md:grid-cols-12">
                <button
                  data-testid={`lead-expand-${q.id}`}
                  onClick={() => setOpenId(open ? null : q.id)}
                  className="col-span-2 flex items-center gap-3 text-left md:col-span-4"
                >
                  <ChevronDown size={14} className={`shrink-0 text-[#8A837B] transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
                  <span>
                    <span className="block text-sm font-semibold text-[#141210]">{q.first_name} {q.last_name}</span>
                    <span className="block text-xs text-[#6B655F]">{q.email}</span>
                  </span>
                </button>
                <span className="hidden text-xs text-[#6B655F] md:col-span-2 md:block">{q.topic || "—"}</span>
                <span className={`inline-block w-fit px-2 py-1 text-[10px] font-bold uppercase tracking-[0.12em] md:col-span-1 ${chipCls(stage)}`}>
                  {label(stage)}
                </span>
                <select
                  data-testid={`lead-stage-${q.id}`}
                  value={stage}
                  onChange={(e) => patch(q.id, { stage: e.target.value })}
                  className={`${selectCls} md:col-span-2`}
                >
                  {LEAD_STAGES.map((s) => (
                    <option key={s} value={s}>{label(s)}</option>
                  ))}
                </select>
                <select
                  data-testid={`lead-assignee-${q.id}`}
                  value={q.assigned_to || ""}
                  onChange={(e) => patch(q.id, { assigned_to: e.target.value })}
                  className={`${selectCls} md:col-span-2`}
                >
                  <option value="">Unassigned</option>
                  {TEAM.map((m) => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
                <span className="hidden text-right text-[11px] text-[#8A837B] md:col-span-1 md:block">
                  {new Date(q.created_at).toLocaleDateString()}
                </span>
              </div>
              {open && (
                <div className="border-t border-[#F1EDE6] bg-[#FAF8F5] px-5 py-5 md:pl-12" data-testid={`lead-detail-${q.id}`}>
                  <p className="max-w-3xl text-sm leading-relaxed text-[#141210]">{q.message}</p>
                  <div className="mt-4 flex flex-wrap gap-x-6 gap-y-1 text-xs text-[#6B655F]">
                    {q.phone && <span>Phone: {q.phone}</span>}
                    <span>Language: {(q.lang || "en").toUpperCase()}</span>
                  </div>
                  <div className="mt-5">
                    <h4 className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#8A837B]">Notes</h4>
                    <div className="mt-2 flex flex-col gap-2">
                      {(q.notes || []).map((n, i) => (
                        <p key={i} className="text-xs text-[#141210]">
                          <span className="font-semibold">{n.by}</span> · {new Date(n.at).toLocaleString()} — {n.text}
                        </p>
                      ))}
                      {(q.notes || []).length === 0 && <p className="text-xs text-[#8A837B]">No notes yet.</p>}
                    </div>
                    <div className="mt-3 flex max-w-xl gap-2">
                      <input
                        data-testid={`lead-note-input-${q.id}`}
                        value={open ? note : ""}
                        onChange={(e) => setNote(e.target.value)}
                        placeholder="Add a note…"
                        className={inputCls}
                      />
                      <button
                        data-testid={`lead-note-add-${q.id}`}
                        onClick={async () => {
                          if (!note.trim()) return;
                          await patch(q.id, { note: note.trim() });
                          setNote("");
                        }}
                        className="flex items-center gap-2 bg-[#141210] px-4 py-2 text-[11px] font-bold uppercase tracking-[0.12em] text-[#FAF8F5] transition-colors duration-300 hover:bg-[#581820]"
                      >
                        <Send size={12} /> Add
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
